import {
  obligationListSchema,
  obligationPeriodListSchema,
  obligationPeriodSchema,
  obligationSchema,
  type ChangeObligationStatusRequest,
  type CreateObligationRequest,
  type UpdateObligationPeriodRequest,
  type UpdateObligationRequest,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

export const obligationsKey = (workspaceId: string, founderId: string) =>
  [...workspaceKey(workspaceId), 'obligations', founderId] as const;

export function useObligations(workspaceId: string, founderId: string, enabled = true) {
  return useQuery({
    queryKey: obligationsKey(workspaceId, founderId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'founders', founderId, 'obligations'), {
        schema: obligationListSchema,
        signal,
      }),
    enabled,
  });
}

export function useObligationPeriods(
  workspaceId: string,
  founderId: string,
  window: { from?: string; to?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: [...obligationsKey(workspaceId, founderId), 'periods', window] as const,
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'founders', founderId, 'obligation-periods'), {
          from: window.from,
          to: window.to,
        }),
        { schema: obligationPeriodListSchema, signal },
      ),
    enabled,
  });
}

export function useUpdateObligationPeriod(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ periodId, body }: { periodId: string; body: UpdateObligationPeriodRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'obligation-periods', periodId, 'status'),
        body,
        obligationPeriodSchema,
      ),
  );
}

export function useCreateObligation(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ founderId, body }: { founderId: string; body: CreateObligationRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'founders', founderId, 'obligations'),
        body,
        obligationSchema,
      ),
  );
}

export function useUpdateObligation(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ obligationId, body }: { obligationId: string; body: UpdateObligationRequest }) =>
      apiPatch(workspacePath(workspaceId, 'obligations', obligationId), body, obligationSchema),
  );
}

export function useChangeObligationStatus(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ obligationId, body }: { obligationId: string; body: ChangeObligationStatusRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'obligations', obligationId, 'status'),
        body,
        obligationSchema,
      ),
  );
}
